import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { 
  TextField, 
  Button, 
  Card, 
  CardContent, 
  Typography, 
  Box,
  Alert,
  Container,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  InputAdornment,
  Grid,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { authenticatedFetch } from '../../utils/api';
import { useTranslation } from '../../i18n';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const employeeSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  phone: z.string().optional().or(z.literal('')),
  address: z.string().optional().or(z.literal('')),
  salary_type: z.enum(['monthly', 'shift_based']),
  monthly_salary: z.number().optional().nullable(),
  pay_per_shift: z.number().optional().nullable(),
}).refine((data) => {
  if (data.salary_type === 'monthly' && (!data.monthly_salary || data.monthly_salary <= 0)) {
    return false;
  }
  return true;
}, {
  message: "Monthly salary is required for monthly salaried employees",
  path: ["monthly_salary"],
}).refine((data) => {
  if (data.salary_type === 'shift_based' && (!data.pay_per_shift || data.pay_per_shift <= 0)) {
    return false;
  }
  return true;
}, {
  message: "Pay per shift is required for shift-based employees",
  path: ["pay_per_shift"],
});

type EmployeeFormData = z.infer<typeof employeeSchema>;

const CreateEmployeeForm = () => {
  const navigate = useNavigate();
  const { translations: t } = useTranslation();
  const [error, setError] = React.useState<string>('');
  
  const { 
    control, 
    handleSubmit, 
    watch,
    formState: { errors, isSubmitting }
  } = useForm<EmployeeFormData>({
    resolver: zodResolver(employeeSchema),
    defaultValues: {
      name: '',
      phone: '',
      address: '',
      salary_type: 'shift_based',
      monthly_salary: null,
      pay_per_shift: null,
    }
  });

  const salaryType = watch('salary_type');

  const onSubmit = async (data: EmployeeFormData) => {
    try {
      const response = await authenticatedFetch(`${API_URL}/employees`, {
        method: 'POST',
        body: JSON.stringify({ employee: data })
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(JSON.stringify(errorData));
      }

      await response.json();
      navigate('/employees');
    } catch (err) {
      console.error('Error:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  return (
    <Container maxWidth="sm">
      <Card sx={{ mt: 4 }}>
        <CardContent>
          <Typography variant="h5" component="h2" gutterBottom>
            {t.employees.createNew}
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ mt: 2 }}>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t.employees.name}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  fullWidth
                  margin="normal"
                />
              )}
            />

            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={`${t.employees.phone} (${t.optional})`}
                  error={!!errors.phone}
                  helperText={errors.phone?.message}
                  fullWidth
                  margin="normal"
                />
              )}
            />

            <Controller
              name="address"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={`${t.companies.address} (${t.optional})`}
                  error={!!errors.address}
                  helperText={errors.address?.message}
                  fullWidth
                  margin="normal"
                  multiline
                  rows={2}
                />
              )}
            />

            <Controller
              name="salary_type"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth margin="normal" error={!!errors.salary_type}>
                  <InputLabel>{t.finance?.salaryType || "Salary Type"}</InputLabel>
                  <Select
                    {...field}
                    label={t.finance?.salaryType || "Salary Type"}
                  >
                    <MenuItem value="shift_based">{t.finance?.shiftBased || "Shift Based"}</MenuItem>
                    <MenuItem value="monthly">{t.finance?.monthly || "Monthly"}</MenuItem>
                  </Select>
                  {errors.salary_type && (
                    <FormHelperText>{errors.salary_type.message}</FormHelperText>
                  )}
                </FormControl>
              )}
            />

            {salaryType === 'monthly' && (
              <Controller
                name="monthly_salary"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value || ''}
                    onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : null)}
                    label={t.employees?.monthlySalary || "Monthly Salary"}
                    type="number"
                    error={!!errors.monthly_salary}
                    helperText={errors.monthly_salary?.message}
                    fullWidth
                    margin="normal"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    }}
                  />
                )}
              />
            )}

            {salaryType === 'shift_based' && (
              <Controller
                name="pay_per_shift"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value || ''}
                    onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : null)}
                    label={t.employees?.payPerShift || "Pay Per Shift"}
                    type="number"
                    error={!!errors.pay_per_shift}
                    helperText={errors.pay_per_shift?.message}
                    fullWidth
                    margin="normal"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                      endAdornment: <InputAdornment position="end">/shift</InputAdornment>,
                    }}
                  />
                )}
              />
            )}

            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary"
                fullWidth
                disabled={isSubmitting}
              >
                {isSubmitting ? t.loading : t.employees.createNew}
              </Button>
              
              <Button 
                variant="outlined"
                fullWidth
                onClick={() => navigate('/employees')}
              >
                {t.cancel}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default CreateEmployeeForm;
